<template>
    <div class="login-form">
        <div class="wrapper">
            <div class="form-header">
                <div class="form-title">
                    <ButtonRound
                        v-if="backButtonInTitle && page === 'EMAIL_AND_NAME'"
                        class="button-back-title"
                        icon="angle-left"
                        type="link"
                        aria-label="Go back one level"
                        @click="goBack"
                    />

                    <slot name="title">
                        <h3 class="title">
                            Sign in to see your designs and quotations
                        </h3>
                    </slot>
                </div>

                <div
                    v-if="$slots['description']"
                    class="form-description"
                >
                    <slot name="description"></slot>
                </div>
            </div>

            <form
                class="form"
                @submit.prevent="handleFormSubmit"
            >
                <fieldset
                    v-show="page === 'SSO'"
                    class="page sso"
                    :disabled="fetching || fetchingMagicLinkMutation"
                >
                    <div class="sso-wrapper">
                        <GoogleSignIn
                            @submit="loginWithGoogle"
                            @fail="handleLoginWithGoogleFail"
                            @click="trackClickLogin('google')"
                        />

                        <FacebookSignIn
                            @submit="loginWithFacebook"
                            @fail="handleLoginWithFacebookFail"
                            @click="trackClickLogin('facebook')"
                        />
                    </div>

                    <div class="separator">
                        <span class="text">or</span>
                    </div>

                    <TextField
                        :id="page === 'SSO' ? 'form-email' : ''"
                        v-model="identifier"
                        v-qa="page === 'SSO' ? 'login-email' : ''"
                        :error="continueWithEmailErrorMessage"
                        :placeholder="t('login.email_placeholder')"
                        lazy
                        class="text-field"
                        required
                        type="email"
                    />

                    <Honeypot v-model="honeypot" />

                    <ThButton
                        v-qa="'login-email-continue'"
                        class="th-button"
                        full-width
                        type="solid"
                        @click="continueWithEmail"
                    >
                        {{ t('login.continue') }}
                    </ThButton>
                </fieldset>

                <fieldset
                    v-show="page === 'EMAIL_AND_NAME'"
                    class="page email-and-name"
                    :disabled="fetching"
                >
                    <TextField
                        :id="page === 'EMAIL_AND_NAME' ? 'form-email' : ''"
                        v-model="identifier"
                        v-qa="page === 'EMAIL_AND_NAME' ? 'login-email' : ''"
                        :error="emailError || sendMagicLinkNoEmailError"
                        :placeholder="t('login.email_placeholder')"
                        lazy
                        class="text-field"
                        required
                        type="email"
                    />

                    <div class="reset-password">
                        <ThLink
                            :to="routes.forgottenPassword()"
                            @click="trackForgotPasswordClick"
                        >
                            {{ t('login.forgot_password_button') }}
                        </ThLink>
                    </div>

                    <TextField
                        id="form-password"
                        v-model="password"
                        v-qa="'login-password'"
                        :error="errorMessage"
                        :placeholder="t('login.password_placeholder')"
                        class="text-field"
                        required
                        type="password"
                    />

                    <ThButton
                        v-qa="'login-email-submit'"
                        :loading="fetching"
                        class="th-button"
                        full-width
                        submit
                        type="solid"
                    >
                        {{ t('login.login_button') }}
                    </ThButton>

                    <div class="separator">
                        <span class="text">or</span>
                    </div>

                    <div class="sso-wrapper">
                        <ThButton
                            class="magic-link-button"
                            color="var(--slate)"
                            full-width
                            type="bordered"
                            @click="submitMagicLink"
                        >
                            {{ t('login.magic_link_button') }}
                        </ThButton>
                    </div>
                </fieldset>

                <div
                    v-show="page === 'MAGIC_LINK'"
                    class="page magic-link"
                >
                    <TextField
                        id="magic-link-email-readonly"
                        v-model="identifier"
                        readonly
                        :placeholder="t('login.email_placeholder')"
                        class="text-field"
                        type="email"
                    />

                    <img
                        class="illustration"
                        src="/src/assets/mail_sent.svg"
                    />

                    {{ t('login.magic_link.success_message') }}
                </div>
            </form>
        </div>

        <div
            v-if="page !== 'MAGIC_LINK' && !hideSwitchToRegistration"
            class="registration-switch"
        >
            {{ t('login.switch_to_registration.label') }}

            <button
                class="button"
                type="button"
                @click.stop.prevent="switchToRegistration"
            >
                {{ t('login.switch_to_registration.button') }}
            </button>
        </div>

        <ButtonRound
            v-if="!backButtonInTitle && page === 'EMAIL_AND_NAME'"
            class="button-back"
            icon="angle-left"
            type="link"
            aria-label="Go back one level"
            @click="goBack"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

import type {
    LoginResponseFailureFragment,
    LoginResponseSuccessFragment,
} from '@/graphql/api'
import { assertNever, logUnexpectedError } from '@/helpers'
import useLogin from '@/composables/auth/useLogin'
import { useRoutes } from '@/router/routes'
import { useMagicLinkRequestMutation } from '@/graphql/api/magic-link/magicLinkRequest.mutation.generated'
import {
    useMergedCollection,
    useResponseError,
    useSimpleErrorCollection,
} from '@/helpers/errors'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl'
import { useEmailError } from '@/composables/useEmailError'
import { useAuthTracking, type SsoProvider } from './useAuthTracking'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import type { CombinedError } from '@urql/vue'
import { useEmailFieldError } from './useEmailFieldError'

import TextField from '@/components/ui/inputs/TextField.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import GoogleSignIn from './sso/GoogleSignIn.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import FacebookSignIn from './sso/FacebookSignIn.vue'
import Honeypot from './Honeypot.vue'

defineProps<{
    hideSwitchToRegistration?: boolean
    backButtonInTitle?: boolean
}>()

const emit = defineEmits<{
    login: [v: LoginResponseSuccessFragment]
    error: [v: LoginResponseFailureFragment]
    'switchToRegistration': []
}>()

const {
    trackSso,
    trackEmail,
    trackClick,
    trackAuthNavigation,
    trackForgotPasswordClick,
    trackMagicLinkClick,
    trackHoneypot,
} = useAuthTracking()

const {
    identifier,
    password,
    login: loginMutation,
    loginWithGoogle: loginWithGoogleMutation,
    loginWithFacebook: loginWithFacebookMutation,
    fetching,
    errorMessage,
    emailError,
} = useLogin()

const { email } = storeToRefs(useCustomerRegistrationInputStore())

if (email.value) {
    identifier.value = email.value
}

const handleLoginResponse = (
    data:
        | LoginResponseSuccessFragment
        | LoginResponseFailureFragment
        | undefined
        | CombinedError,
    provider: SsoProvider | 'email'
) => {
    if (!data) {
        return
    }

    if (!('__typename' in data)) {
        if (provider !== 'email') {
            trackSso(provider, 'failure', 'login')
        }

        return
    }

    switch (data.__typename) {
        case 'LoginResponseSuccess':
            if (provider === 'email') {
                trackEmail('success', 'login')
            } else {
                trackSso(provider, 'success', 'login')
            }

            emit('login', data)
            break
        case 'LoginResponseFailure':
            if (provider !== 'email') {
                trackSso(provider, 'failure', 'login')
            }

            emit('error', data)
            break
        default:
            assertNever(data, data)
            break
    }
}

const loginWithEmail = () =>
    loginMutation()
        ?.then((res) => {
            handleLoginResponse(res, 'email')
        })
        .catch((e) => logUnexpectedError('Sign in error', e))

const trackClickLogin = (provider: Parameters<typeof trackClick>[0]) =>
    trackClick(provider, 'login')

const handleLoginWithGoogleFail = () => {
    trackSso('google', 'failure', 'login')
}

const loginWithGoogle = () =>
    loginWithGoogleMutation()
        .then((res) => {
            handleLoginResponse(res, 'google')
        })
        .catch(() => handleLoginWithGoogleFail())

const handleLoginWithFacebookFail = () => {
    trackSso('facebook', 'failure', 'login')
}

const loginWithFacebook = () =>
    loginWithFacebookMutation()
        .then((res) => {
            handleLoginResponse(res, 'facebook')
        })
        .catch(() => handleLoginWithFacebookFail())

const page = ref<'SSO' | 'EMAIL_AND_NAME' | 'MAGIC_LINK'>('SSO')

const honeypot = ref('')

const goToPageEmailAndName = () => {
    if (honeypot.value) {
        trackHoneypot('login')

        return
    }

    page.value = 'EMAIL_AND_NAME'

    trackClickLogin('email')
}

const {
    errorsWithFeValidation: continueWithEmailError,
    submit: continueWithEmail,
} = useEmailFieldError(identifier, ref(''), goToPageEmailAndName)

const continueWithEmailErrorMessage = computed(() =>
    continueWithEmailError.value.last('customer.email')
)

const goToPageSso = () => {
    page.value = 'SSO'
}

const goToPageMagicLink = () => {
    page.value = 'MAGIC_LINK'
}

const goBack = () => {
    switch (page.value) {
        case 'MAGIC_LINK':
            goToPageEmailAndName()
            return
        case 'EMAIL_AND_NAME':
            goToPageSso()
            return
        case 'SSO':
            return
        default:
            assertNever(page.value, undefined)
    }
}

const switchToRegistration = () => {
    emit('switchToRegistration')

    trackAuthNavigation('registration')
}

const { error: emailErrorMagicLink, validate: emailValidate } = useEmailError(
    identifier,
    ['input', 'email']
)
const mutation = useMagicLinkRequestMutation()
const { fetching: fetchingMagicLinkMutation, executeMutation } = mutation

const collection = useSimpleErrorCollection([emailErrorMagicLink])
const errorsWithFeValidation = useMergedCollection(useResponseError(mutation), [
    collection,
])

const magicLinkRequestError = computed(() =>
    errorsWithFeValidation.value.last('input.email')
)

const absoluteUrl = useAbsoluteUrl()
const sendMagicLinkNoEmailError = ref('')

watch(identifier, (email) => {
    if (email) {
        sendMagicLinkNoEmailError.value = ''
    }
})

const submitMagicLink = () => {
    const emailValue = emailValidate()

    if (!emailValue) {
        sendMagicLinkNoEmailError.value = 'Please, enter a valid email address.'

        return
    }

    trackMagicLinkClick()

    executeMutation({
        input: {
            email: emailValue,
            redirect: absoluteUrl(routes.profile.magicLinkBase()).toString(),
        },
    }).then(() => {
        if (!magicLinkRequestError.value) {
            goToPageMagicLink()
        }
    })
}

const handleFormSubmit = () =>
    page.value === 'SSO'
        ? continueWithEmail()
        : page.value === 'EMAIL_AND_NAME'
          ? loginWithEmail()
          : undefined

const routes = useRoutes()
const { t } = useI18n()
</script>

<style lang="scss" scoped>
.login-form {
    height: 100%;
    width: 100%;
    .wrapper {
        width: 100%;
        height: 100%;
        max-width: 500px;
        margin: 0 auto;

        position: relative;
        display: flex;
        flex-direction: column;
        align-self: flex-start;
        justify-content: center;

        font-family: $sectra-font-stack;
        font-style: normal;
        font-weight: 400;
        color: $slate;
        font-size: 0.75rem;
        line-height: 1.4em;
    }

    .form-header {
        width: 100%;
        padding: 0 20px;
        margin: 0 auto;

        @include min-width(520) {
            margin: 0 auto 40px;
        }

        .form-title {
            font-family: $parcel-font-stack;
            font-size: 2rem;
            line-height: 1em;
            color: $blue;
            text-align: center;
            margin: 0 0 30px;

            .button-back-title {
                display: block;
                align-self: start;
            }
        }

        .form-description {
            text-align: center;
            line-height: 1.4em;
            margin-bottom: 16px;
        }
    }

    .form {
        padding: 0 20px;
        margin: 0 auto;
        width: 100%;
        max-width: 380px;

        display: flex;
        flex-direction: column;
        align-self: flex-start;

        .page {
            height: 100%;

            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;

            > * {
                width: 100%;
            }

            &.email-and-name {
                .reset-password {
                    margin-bottom: -8px;
                    text-align: right;
                    color: $blue;
                }
            }

            &.magic-link {
                text-align: center;

                .illustration {
                    margin-bottom: 24px;
                    max-width: 212px;
                    aspect-ratio: 1/1;
                }
            }
        }

        .page:disabled {
            opacity: 0.5;
        }

        .sso-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 16px;
            width: 100%;

            .magic-link-button {
                background-color: $white;

                &:hover {
                    color: $slate;
                }
            }
        }

        .separator {
            text-align: center;
            display: flex;
            align-items: center;
            max-width: 200px;
            margin: 0 auto;
            text-transform: uppercase;
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-size: 0.875rem;

            &:before,
            &:after {
                content: '';
                display: inline-block;
                width: 50%;
                height: 1px;
                background-color: $grey-200;
            }

            .text {
                display: block;
                margin: 0 10px;
            }
        }
    }

    .registration-switch {
        position: absolute;
        bottom: 10px;
        width: 100%;
        text-align: center;

        font-family: $sectra-font-stack;
        font-style: normal;
        font-weight: 400;
        color: $slate;
        font-size: 0.75rem;
        line-height: 1.4em;

        .button {
            position: relative;
            color: $blue;
            cursor: pointer;

            &::after {
                position: absolute;
                top: 0.5px;
                font-family: $icon-font;
                content: $icon-angle-right-bold;
                font-size: 12px;
            }
        }
    }
}

.button-back {
    position: absolute;
    top: 15px;
    left: 20px;
    z-index: 1;
}
</style>
